import React, { useEffect, useState } from 'react';
import { ExternalLink, MapPin, Calendar, Trophy, Users, Ticket, Newspaper, ArrowUpRight, Info, ArrowUp, ArrowDown, Minus, ChevronDown } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_STYLES = {
  W: { bg: 'bg-emerald-500', text: 'text-white', label: 'Win' },
  D: { bg: 'bg-slate-400',   text: 'text-white', label: 'Draw' },
  L: { bg: 'bg-rose-500',    text: 'text-white', label: 'Loss' },
};

const fmtDate = (iso, opts) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', opts || { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return iso.slice(0, 10); }
};

const ScoreCell = ({ hs, as_ }) => {
  if (hs === null || hs === undefined) return <span className="text-slate-400 text-xs">vs</span>;
  return <span className="font-mono font-bold text-slate-900">{hs}–{as_}</span>;
};

const minuteLabel = (g) => `${g.minute ?? ''}${g.extra ? `+${g.extra}` : ''}'`;

/**
 * Movement — whether the result moved Saints up or down the table. Positions
 * are rebuilt from every league result up to that kick-off, so older matches
 * show where the club actually stood at the time, not where they are today.
 */
const Movement = ({ position }) => {
  if (!position?.after) return null;
  const { before, after, change } = position;
  const tone = !change ? 'text-slate-400' : change > 0 ? 'text-emerald-600' : 'text-rose-600';
  const Icon = !change ? Minus : change > 0 ? ArrowUp : ArrowDown;
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${tone}`}
          title={before ? `${ordinal(before)} before this match, ${ordinal(after)} after` : `${ordinal(after)} in the league`}
          data-testid="result-movement">
      <Icon size={12} strokeWidth={3} />
      {ordinal(after)}
      {change ? <span className="text-slate-400">({change > 0 ? '+' : ''}{change})</span> : null}
    </span>
  );
};

const ordinal = (n) => {
  if (n % 100 >= 10 && n % 100 <= 20) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10]) || 'th'}`;
};

/**
 * Lineup — the starting eleven with shirt numbers, plus who came off the
 * bench and when. Folded away so five results do not become a wall of names.
 */
const Lineup = ({ fixture }) => {
  const [open, setOpen] = useState(false);
  const lineup = fixture.lineup || {};
  const xi = lineup.start_xi || [];
  if (!xi.length) return null;
  const subsOn = (fixture.subs || []).filter((s) => s.is_st_mirren && s.on);

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold
                   text-slate-400 hover:text-slate-700 transition-colors"
        data-testid={`lineup-toggle-${fixture.id}`}
      >
        Line-up{lineup.formation ? ` · ${lineup.formation}` : ''}
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-2 text-xs" data-testid={`lineup-${fixture.id}`}>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {xi.map((p, i) => (
              <span key={i} className="text-slate-700">
                <span className="text-slate-400 tabular-nums">{p.number}</span> {p.name}
              </span>
            ))}
          </div>
          {subsOn.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Bench</span>
              {subsOn.map((s, i) => (
                <span key={i} className="text-slate-600">
                  {s.on} <span className="text-slate-400">for {s.off} {minuteLabel(s)}</span>
                </span>
              ))}
            </div>
          )}
          {lineup.coach && <p className="text-slate-400">Manager: {lineup.coach}</p>}
        </div>
      )}
    </div>
  );
};

/**
 * MatchDetail — goalscorers and bookings under a result, St Mirren's own in
 * black and the opposition's greyed, so the Buddies' names read first.
 */
const MatchDetail = ({ fixture }) => {
  const goals = (fixture.goals || []).filter((g) => g.player);
  const cards = (fixture.cards || []).filter((c) => c.player);
  if (!goals.length && !cards.length) return null;

  return (
    <div className="mt-2.5 pt-2.5 border-t border-slate-200 space-y-1.5" data-testid={`result-detail-${fixture.id}`}>
      {goals.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Goals</span>
          {goals.map((g, i) => (
            <span key={i} className={g.is_st_mirren ? 'text-slate-900 font-medium' : 'text-slate-500'}>
              {g.player}
              <span className="text-slate-400">
                {' '}{minuteLabel(g)}{g.own_goal ? ' og' : ''}{g.penalty ? ' pen' : ''}
              </span>
            </span>
          ))}
        </div>
      )}
      {cards.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Cards</span>
          {cards.map((c, i) => (
            <span key={i} className={`flex items-center gap-1 ${c.is_st_mirren ? 'text-slate-900' : 'text-slate-500'}`}>
              <span className={`inline-block w-2 h-3 rounded-sm ${c.card === 'red' ? 'bg-rose-600' : 'bg-amber-400'}`} />
              {c.player}
              <span className="text-slate-400">{minuteLabel(c)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const StMirrenPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/st-mirren`, { cache: 'no-store' })
      .then((r) => r.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const team = data?.team?.team || {};
  const venue = data?.team?.venue || {};
  const seasonLabel = data?.season_label || '';
  const preseason = data?.preseason;
  const ticketsUrl = 'https://tickets.stmirren.com';
  const officialUrl = 'https://stmirren.com';

  return (
    <div className="min-h-screen bg-white" data-testid="st-mirren-page">
      <SEO
        title="St Mirren — Fixtures, Results & News | Paisley.org.uk"
        description="The factual home for St Mirren FC on paisley.org.uk: fixtures, results, league table and the latest news for the Buddies. Updated daily."
        keywords="St Mirren, Buddies, SMISA Stadium, Scottish Premiership, Paisley football, St Mirren fixtures, St Mirren results"
        url="/st-mirren"
        image="/images/og/roads.png"
      />
      <Header />

      {/* Hero — black/white stripes nod to the kit */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, transparent 0 36px, rgba(255,255,255,0.9) 36px 72px)',
          }}
        />
        <div className="absolute -top-32 -right-20 w-[440px] h-[440px] rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="flex flex-wrap items-center gap-6">
            {team.logo && (
              <img
                src={team.logo}
                alt={`${team.name} badge`}
                className="w-28 h-28 lg:w-32 lg:h-32 object-contain drop-shadow-xl"
                data-testid="club-badge"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-rose-400 mb-2">
                The Buddies · Founded {team.founded || 1877}
              </p>
              <h1 className="text-5xl lg:text-6xl font-bold tracking-[-0.04em] mb-3" data-testid="club-h1">
                St Mirren
              </h1>
              <p className="text-slate-200 text-base max-w-2xl">
                The factual home of St Mirren on paisley.org.uk — fixtures, results, league standing and headlines.
                {preseason && ' The 2026/27 season kicks off 2nd August.'}
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <a href={ticketsUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white px-4 py-2 rounded-full text-sm font-bold transition-colors"
                   data-testid="tickets-cta">
                  <Ticket size={15} /> Tickets <ArrowUpRight size={13} />
                </a>
                <a href={officialUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors backdrop-blur">
                  Official site <ArrowUpRight size={13} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pre-season banner — shown mid-June through early August. Once the
          Premiership fixtures are confirmed on the schedule below, this just
          serves as a heads-up rather than a "no data" apology. */}
      {preseason && (
        <section className="bg-amber-50 border-y border-amber-200" data-testid="preseason-banner">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-start gap-3 text-sm">
            <Info size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-amber-900">
              <span className="font-bold">Pre-season:</span> the {seasonLabel} kicks off in early August. League Cup group games, friendlies and any European ties are already listed below, with results and league table updated live via api-football and BBC Sport.
            </p>
          </div>
        </section>
      )}

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-12">
        {/* Club facts */}
        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="club-facts">
          {[
            { icon: MapPin, label: 'Ground', value: venue.name || 'The SMISA Stadium' },
            { icon: Users, label: 'Capacity', value: venue.capacity ? venue.capacity.toLocaleString() : '8,016' },
            { icon: Calendar, label: 'Founded', value: team.founded || 1877 },
            { icon: Trophy, label: 'League', value: 'Scottish Premiership' },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="bg-slate-50 rounded-2xl p-4">
              <Icon size={16} className="text-slate-400 mb-2" />
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{label}</p>
              <p className="font-bold text-slate-900">{value}</p>
            </div>
          ))}
        </section>

        {/* Recent form pills */}
        {data?.form?.length > 0 && (
          <section data-testid="form-row">
            <h2 className="text-xl font-bold text-slate-900 mb-1">Recent form</h2>
            <p className="text-sm text-slate-500 mb-4">Last 5 league matches · most recent first</p>
            <div className="flex gap-2">
              {data.form.map((r, i) => {
                const s = FORM_STYLES[r] || FORM_STYLES.D;
                return (
                  <span key={i}
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold ${s.bg} ${s.text}`}
                    title={s.label}>{r}</span>
                );
              })}
            </div>
          </section>
        )}

        {/* Two-column: Upcoming + Results */}
        <section className="grid lg:grid-cols-2 gap-8">
          <div data-testid="upcoming-section">
            <h2 className="text-xl font-bold text-slate-900 mb-1">Upcoming fixtures</h2>
            <p className="text-sm text-slate-500 mb-4">{seasonLabel}</p>
            {data?.upcoming?.length > 0 ? (
              <div className="space-y-2">
                {data.upcoming.map((f) => (
                  <div key={f.id} className="bg-slate-50 rounded-xl p-3 flex items-center gap-3 text-sm" data-testid={`upcoming-${f.id}`}>
                    <div className="text-center w-14 shrink-0">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{fmtDate(f.date, { day: 'numeric', month: 'short' })}</p>
                      <p className="text-[10px] text-slate-400">{fmtDate(f.date, { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    {f.opponent_logo && <img src={f.opponent_logo} alt="" className="w-8 h-8 object-contain" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{f.is_home ? 'vs' : 'at'} {f.opponent}</p>
                      <p className="text-xs text-slate-500">{f.competition}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-xl p-6 text-center text-sm text-slate-500">
                Fixtures for 2026/27 not yet published.
              </div>
            )}
          </div>
          <div data-testid="results-section">
            <h2 className="text-xl font-bold text-slate-900 mb-1">Recent results</h2>
            <p className="text-sm text-slate-500 mb-4">{seasonLabel}</p>
            {data?.results?.length > 0 ? (
              <div className="space-y-2">
                {data.results.map((f) => (
                  <div key={f.id} className="bg-slate-50 rounded-xl p-3 text-sm" data-testid={`result-${f.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="text-center w-14 shrink-0">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{fmtDate(f.date, { day: 'numeric', month: 'short' })}</p>
                      </div>
                      {f.opponent_logo && <img src={f.opponent_logo} alt="" className="w-8 h-8 object-contain" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{f.is_home ? 'vs' : 'at'} {f.opponent}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-slate-500">{f.competition}</p>
                          <Movement position={f.position} />
                        </div>
                      </div>
                      <ScoreCell hs={f.home_score} as_={f.away_score} />
                    </div>
                    <MatchDetail fixture={f} />
                    <Lineup fixture={f} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-xl p-6 text-center text-sm text-slate-500">No recent results.</div>
            )}
          </div>
        </section>

        {/* League table */}
        {data?.table?.length > 0 && (
          <section data-testid="league-table">
            <h2 className="text-xl font-bold text-slate-900 mb-1">{seasonLabel} table</h2>
            <p className="text-sm text-slate-500 mb-4">
              St Mirren highlighted in red
              {(data.table || []).length > 0 && (data.table || []).length < 10 && (
                <span className="block mt-1 text-amber-700" data-testid="st-mirren-table-partial">
                  Showing the top {(data.table || []).length} only — the current data
                  source returns a limited table.
                </span>
              )}
            </p>
            <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Team</th>
                    <th className="px-2 py-2 text-center hidden sm:table-cell">P</th>
                    <th className="px-2 py-2 text-center hidden sm:table-cell">W</th>
                    <th className="px-2 py-2 text-center hidden sm:table-cell">D</th>
                    <th className="px-2 py-2 text-center hidden sm:table-cell">L</th>
                    <th className="px-2 py-2 text-center hidden md:table-cell">GD</th>
                    <th className="px-3 py-2 text-right font-bold">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {data.table.map((t) => (
                    <tr key={t.id}
                      className={`border-t border-slate-100 ${t.is_st_mirren ? 'bg-rose-50 ring-1 ring-rose-200' : 'hover:bg-slate-50'}`}
                      data-testid={`table-row-${t.id}`}>
                      <td className="px-3 py-2 font-semibold text-slate-500">{t.rank}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {t.logo && <img src={t.logo} alt="" className="w-5 h-5 object-contain shrink-0" />}
                          <span className={`truncate ${t.is_st_mirren ? 'font-bold text-rose-700' : 'text-slate-900'}`}>{t.name}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center hidden sm:table-cell">{t.played}</td>
                      <td className="px-2 py-2 text-center hidden sm:table-cell">{t.win}</td>
                      <td className="px-2 py-2 text-center hidden sm:table-cell">{t.draw}</td>
                      <td className="px-2 py-2 text-center hidden sm:table-cell">{t.lose}</td>
                      <td className={`px-2 py-2 text-center hidden md:table-cell ${t.gd > 0 ? 'text-emerald-600' : t.gd < 0 ? 'text-rose-600' : 'text-slate-500'}`}>{t.gd > 0 ? `+${t.gd}` : t.gd}</td>
                      <td className="px-3 py-2 text-right font-bold text-slate-900">{t.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-3">{data.attribution?.stats}</p>
          </section>
        )}

        {/* BBC News */}
        <section data-testid="news-section">
          <div className="flex items-center gap-2 mb-1">
            <Newspaper size={18} className="text-slate-500" />
            <h2 className="text-xl font-bold text-slate-900">Latest news</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">Headlines from BBC Sport</p>
          {data?.news?.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {data.news.slice(0, 8).map((n, i) => (
                <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
                  className="block bg-slate-50 hover:bg-slate-100 rounded-xl p-4 transition-colors group"
                  data-testid={`news-${i}`}>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{n.source}</p>
                  <h3 className="font-bold text-slate-900 leading-snug mb-1 group-hover:text-slate-700">{n.title}</h3>
                  {n.description && <p className="text-sm text-slate-600 line-clamp-2 mb-2">{n.description}</p>}
                  <span className="inline-flex items-center gap-1 text-xs text-rose-600 font-semibold">
                    Read on BBC Sport <ExternalLink size={11} />
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <div className="bg-slate-50 rounded-xl p-6 text-center text-sm text-slate-500">No headlines right now.</div>
          )}
        </section>

        {/* Editorial — Paisley.org.uk staff posts tagged st-mirren */}
        {data?.editorial?.length > 0 && (
          <section data-testid="editorial-section">
            <div className="flex items-center gap-2 mb-1">
              <Newspaper size={18} className="text-cyan-600" />
              <h2 className="text-xl font-bold text-slate-900">From Paisley.org.uk</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">Features, match reports and analysis from our editorial team</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.editorial.map((post, i) => (
                <a
                  key={post.slug}
                  href={`/news/${post.slug}`}
                  className="group block bg-white ring-1 ring-slate-200 hover:ring-cyan-400 hover:shadow-lg rounded-xl overflow-hidden transition-all"
                  data-testid={`editorial-${i}`}
                >
                  {post.featured_image && (
                    <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                      <img src={post.featured_image} alt={post.title} loading="lazy"
                           className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                           onError={(e) => { e.target.style.opacity = '0.2'; }}/>
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 mb-2">Paisley.org.uk</p>
                    <h3 className="font-bold text-slate-900 leading-snug mb-1 group-hover:text-slate-700">{post.title}</h3>
                    {post.excerpt && <p className="text-sm text-slate-600 line-clamp-2">{post.excerpt}</p>}
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}
        <section className="text-xs text-slate-400 text-center pt-8 border-t border-slate-200">
          Unofficial fan-page. Paisley.org.uk is not affiliated with St Mirren Football Club. Statistical data via api-football.com · news via BBC Sport · this page reports facts only.
          {loading && ' · Loading…'}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StMirrenPage;
